import { Formik } from 'formik';
import classNames from 'classnames';
import { useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';

import LFModal from 'components/common/LFModal/LFModal';
import Loading from 'components/common/loading/Loading';
import Input from 'components/common/inputWrapper/components/Input';

import * as toast from 'utils/toast';
import { handleError } from 'utils/errorHandler';

import { addLinkCategorySchema } from 'schemas/addLinkCategorySchema';
import { QUICK_LINKS_QUERY_KEY } from 'hooks/query/useQuickLinksQuery';

import { addQuickLinksCategory, updateQuickLinksCategory } from 'services/quickLinks';
import type { AddLinkCategory, QuickLinkCategoryPayload } from 'interface/link';

import type { LinkLibraryCategorySelection, TriggerBtnProps } from '../types';

type QuickLinkMutationResponse = {
  message: string;
};

interface Props {
  selectedCategory?: LinkLibraryCategorySelection;
  isOpen?: boolean;
  onClose: () => void;
  TriggerBtn?: React.ComponentType<TriggerBtnProps>;
  isEdit: boolean;
  isSubCategory: boolean;
  handleRowClick?: () => void;
}

const AddCategory: React.FC<Props> = ({
  selectedCategory,
  isOpen,
  onClose,
  TriggerBtn,
  isEdit,
  isSubCategory,
  handleRowClick,
}) => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues: AddLinkCategory = {
    name: isEdit ? selectedCategory?.name || '' : '',
  };

  const handleCategory = async (values: AddLinkCategory) => {
    setIsSubmitting(true);

    const parentId = isSubCategory
      ? isEdit
        ? selectedCategory?.parentId
        : selectedCategory?.id
      : selectedCategory?.parentId;

    const categoryData: QuickLinkCategoryPayload = {
      name: values.name,
      parentId,
    };

    try {
      if (isEdit && !selectedCategory?.id) {
        throw new Error('Category id is required to update a quick link category.');
      }

      const data = (
        isEdit
          ? await updateQuickLinksCategory(selectedCategory.id, categoryData)
          : await addQuickLinksCategory(categoryData)
      ) as QuickLinkMutationResponse;

      if (!isEdit && handleRowClick) {
        handleRowClick();
      }

      toast.success({
        title: 'Success',
        message: data.message,
      });

      queryClient.invalidateQueries({ queryKey: QUICK_LINKS_QUERY_KEY });
    } catch (error) {
      handleError(error);
    } finally {
      onClose();
      setIsSubmitting(false);
    }
  };

  const title = useMemo(() => {
    if (isEdit) {
      return isSubCategory ? 'Edit sub-category' : 'Edit category';
    }

    return isSubCategory ? 'Add new sub-category' : 'Add new category';
  }, [isEdit, isSubCategory]);

  return (
    <LFModal
      TriggerBtn={TriggerBtn}
      isOpen={isOpen}
      onClose={onClose}
      closeButton={true}
      title={{
        text: title,
        type: 'info',
      }}
      className="skill-modal"
      htmlContent={
        <Formik<AddLinkCategory>
          initialValues={initialValues}
          onSubmit={handleCategory}
          validationSchema={addLinkCategorySchema}
        >
          {({ handleSubmit, handleBlur, handleChange, errors, values, dirty, touched }) => {
            const nameError = touched.name && typeof errors.name === 'string' ? errors.name : undefined;

            return (
              <form onSubmit={handleSubmit} className="skill-modal__form">
                <div className="skill-modal__form-body">
                  <Input
                    isMandatory
                    label={isSubCategory ? 'Sub-category Name' : 'Category Name'}
                    name="name"
                    placeholder={
                      isSubCategory ? 'Write the name of the sub-category' : 'Write the name of the category'
                    }
                    className="wp-100"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={nameError}
                    disabled={isSubmitting}
                    multiline={false}
                    type="text"
                  />
                </div>

                <div className="skill-modal__footer">
                  <button
                    type="submit"
                    onClick={() => handleSubmit()}
                    className={classNames('btn skill-modal__btn', {
                      'btn--primary': !isSubmitting && dirty,
                      'btn--disabled': isSubmitting || !dirty,
                    })}
                    disabled={isSubmitting || !dirty}
                  >
                    <span className={classNames({ invisible: isSubmitting })}>
                      {isEdit ? 'Edit & close' : 'Add & close'}
                    </span>
                    {isSubmitting && <Loading />}
                  </button>

                  <button
                    type="button"
                    className={classNames('btn ml-10 skill-modal__btn', {
                      'btn--outlined-grey': !isSubmitting,
                      'btn--disabled': isSubmitting,
                    })}
                    onClick={(e) => {
                      e.preventDefault();
                      onClose();
                    }}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            );
          }}
        </Formik>
      }
    />
  );
};

export default AddCategory;
